import { debounce as _debounce } from 'lodash-es';
import { Component } from 'react';
import { connect, FormikContextType } from 'formik';
import {
  serializeForStorage,
  parseStoredDraft,
  decidePersistAction,
  valuesAreEffectivelyEqual,
} from './formik-persist-storage';

type FormikPersistOuterProps = {
  name: string;
  debounce?: number;
  isSessionStorage?: boolean;
  excludeFields?: string[];
  hydrate?: (values: Record<string, unknown>) => Record<string, unknown>;
  onRestore?: (savedAt: number | null, droppedFields: string[]) => void;
};

type FormikPersistProps = FormikPersistOuterProps & {
  formik: FormikContextType<unknown>;
};

class FormikPersist extends Component<FormikPersistProps> {
  static defaultProps = {
    debounce: 300,
    isSessionStorage: false,
  };

  // Snapshot of the values applied by a draft restore, so the resulting Formik
  // update isn't mistaken for a user edit and re-persisted (which would bump the
  // savedAt timestamp on every page load).
  restoredValues?: Record<string, unknown>;

  storage = () => (this.props.isSessionStorage ? window.sessionStorage : window.localStorage);

  saveForm = _debounce((values: Record<string, unknown>) => {
    this.storage().setItem(this.props.name, serializeForStorage(values, this.props.excludeFields));
  }, this.props.debounce);

  clearFormState = () => {
    this.storage().removeItem(this.props.name);
  };

  componentDidUpdate(prevProps: FormikPersistProps) {
    const nextValues = this.props.formik.values as Record<string, unknown>;
    const action = decidePersistAction({
      status: this.props.formik.status,
      prevValues: prevProps.formik.values,
      nextValues,
      initialValues: this.props.formik.initialValues,
      restoredValues: this.restoredValues,
    });

    if (action === 'clear') {
      // Cancel any in-flight save so it can't resurrect the entry we just dropped.
      this.saveForm.cancel();
      this.clearFormState();
    } else if (action === 'save') {
      this.saveForm(nextValues);
    }
  }

  componentDidMount() {
    const maybeState = this.storage().getItem(this.props.name);
    const parsed = parseStoredDraft(maybeState);
    if (!parsed) return;

    const currentValues = this.props.formik.values as Record<string, unknown>;

    // A draft that wouldn't meaningfully change the form (e.g. the user typed
    // into a field and then emptied it before the entry was cleared) isn't
    // worth restoring — drop it silently instead of announcing a restore.
    // Compare in serialized space so dates and other objects line up with how
    // the draft was stored. Drafts with droppedFields still announce, though:
    // they represent user data (e.g. an image upload) that was lost.
    if (parsed.droppedFields.length === 0) {
      const currentSerialized = JSON.parse(
        serializeForStorage(currentValues, this.props.excludeFields)
      ).values as Record<string, unknown>;
      if (valuesAreEffectivelyEqual(parsed.values, currentSerialized)) {
        this.clearFormState();
        return;
      }
    }

    const restored = this.props.hydrate
      ? this.props.hydrate(parsed.values)
      : parsed.values;

    // Merge over the current (initial) values so fields excluded from the draft
    // — e.g. a copied event's currentImage — keep their initial value instead of
    // being dropped when the draft is restored.
    const mergedValues = {
      ...currentValues,
      ...restored,
    };
    this.restoredValues = mergedValues;

    this.props.formik.setFormikState({
      ...this.props.formik,
      values: mergedValues,
    });

    if (this.props.onRestore) {
      this.props.onRestore(parsed.savedAt, parsed.droppedFields);
    }
  }

  render() {
    return null;
  }
}

export default connect<FormikPersistOuterProps>(FormikPersist);
